import { useEffect, useMemo, useState } from "react";
import Sidebar from "../../components/admin/sidebar/Sidebar";
import { ToastContainer } from "react-toastify";
import useDash from "../../assets/css/dash.module.css";
import { FontColor, FourGround } from "../../assets/js/theme";
import {
  ArrowClockwise,
  FilterCircle,
  HddNetworkFill,
  TrashFill,
} from "react-bootstrap-icons";
import { useTranslation } from "react-i18next";
import Table from "../../components/table/Table";
import Footer from "../../components/admin/footer/Footer";
import moment from "moment";
import Loader from "../../components/common/Loader";
import {
  deletedOltUser,
  getOLT,
  getOltUsers,
  getOnuLaserInformation,
} from "../../features/oltApi";
import { useDispatch, useSelector } from "react-redux";
import useISPowner from "../../hooks/useISPOwner";
import { badge } from "../../components/common/Utils";
import DataFilter from "../common/DataFilter";
import useDataState from "../../hooks/useDataState";
import { Accordion } from "react-bootstrap";

const OLTUsers = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  //---> Get user & current user data form useISPOwner hooks
  const { role, ispOwnerId, bpSettings, userData } = useISPowner();

  const filterInputPermission = {
    olt: true,
  };

  //---> Get filtered dataset from useDataState hook
  const { filterOptions, setFilterOption } = useDataState();

  //---> Get ISP owner or reseller olt users from redux store
  const users = useSelector((state) => state?.olt.users);

  //---> Get ISP owner or reseller olt from redux store
  const olts = useSelector((state) => state?.olt.olt);

  //---> Loading State
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [laserLoading, setLaserLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [oltUsers, setOltUser] = useState([]);
  const [activeKey, setActiveKey] = useState("");

  useEffect(() => {
    getOltUsers(ispOwnerId, setIsLoading, dispatch);

    !olts?.length && getOLT(ispOwnerId, setLoading, dispatch);
  }, [ispOwnerId]);

  useEffect(() => {
    if (users) {
      setOltUser(users);
    }
  }, [users]);

  const reloadHandler = () => {
    getOltUsers(ispOwnerId, setIsLoading, dispatch);
  };

  const oltMap = new Map(olts.map((olt) => [String(olt.id), olt]));
  const findOltVendor = (id) => oltMap.get(String(id)) || null;

  const handleOLTUserLaserInformation = (id) => {
    getOnuLaserInformation(dispatch, id, setLaserLoading);
  };

  const handleOLTUserDeleted = (id) => {
    deletedOltUser(dispatch, id, setDeleteLoading);
  };

  const columns = useMemo(
    () => [
      {
        width: "6%",
        Header: t("id"),
        accessor: "customer.customerId",
        Cell: ({ row: { original } }) => (
          <div className="text-center">
            <p>{original?.customer?.customerId}</p>
            <p>{badge(original?.status)}</p>
          </div>
        ),
      },
      {
        width: "12%",
        Header: t("name"),
        accessor: "customer.name",
        Cell: ({ row: { original } }) => (
          <div>
            <p>{original?.customer?.name}</p>
            <p>{original?.address}</p>
          </div>
        ),
      },
      {
        width: "10%",
        Header: t("macAddress"),
        accessor: "macAddress",
        Cell: ({ row: { original } }) => (
          <div>
            <p>{original?.macAddress}</p>
            <p>{original?.onuMacAddress}</p>
          </div>
        ),
      },
      {
        width: "10%",
        Header: t("oltPorts"),
        accessor: "olt",
        Cell: ({ row: { original } }) => (
          <div>
            <p>{findOltVendor(original?.olt)?.name}</p>
            <p>{original?.ports}</p>
          </div>
        ),
      },
      {
        width: "10%",
        Header: t("powerDbm"),
        accessor: "rxPowerDbm",
        Cell: ({ row: { original } }) => (
          <div>
            <p>{original?.rxPowerDbm}</p>
            <p>{original?.txPowerDbm}</p>
          </div>
        ),
      },
      {
        width: "7%",
        Header: t("distance"),
        accessor: "distance",
        Cell: ({ row: { original } }) => (
          <div>
            <p>{original?.distance}</p>
            <p>{original?.deregReason}</p>
          </div>
        ),
      },
      {
        width: "10%",
        Header: t("parameter"),
        accessor: "voltage",
        Cell: ({ row: { original } }) => (
          <div>
            <p>{original?.voltage}</p>
            <p>{original?.temperature}</p>
          </div>
        ),
      },
      {
        width: "7%",
        Header: t("onu"),
        accessor: "onuModel",
        Cell: ({ row: { original } }) => (
          <div>
            <p>{original?.onuModel}</p>
            <p>{original?.onuVendor}</p>
          </div>
        ),
      },
      {
        width: "11%",
        Header: t("lastUpdate"),
        accessor: "updatedAt",
        Cell: ({ cell: { value } }) => {
          return moment(value).format("YYYY/MM/DD hh:mm A");
        },
      },
      {
        width: "5%",
        Header: () => <div className="text-center">{t("action")}</div>,
        id: "option",
        Cell: ({ row: { original } }) => {
          const vendor = findOltVendor(original?.olt);
          const hasCustomer = Boolean(original?.customer?.customerId);
          const hasVendor = Boolean(vendor?.name);

          const showDelete = !hasCustomer || !hasVendor;

          return (
            <div className="d-flex justify-content-center align-items-center gap-2">
              {showDelete ? (
                <button
                  title="Delete User"
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => handleOLTUserDeleted(original?._id)}
                >
                  {deleteLoading ? <Loader /> : <TrashFill size={18} />}
                </button>
              ) : (
                <button
                  title="Sync ONU Laser Information"
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => handleOLTUserLaserInformation(original?._id)}
                >
                  {laserLoading ? <Loader /> : <HddNetworkFill size={18} />}
                </button>
              )}
            </div>
          );
        },
      },
    ],
    [t, olts, laserLoading, deleteLoading],
  );

  return (
    <>
      <Sidebar />
      <ToastContainer position="top-right" theme="colored" />
      <div className={useDash.dashboardWrapper}>
        <div className="container-fluied collector ">
          <div className="container">
            <FontColor>
              <FourGround>
                <div className="collectorTitle d-flex justify-content-between px-4">
                  <h2> {t("oltUsers")} </h2>

                  <div className="d-flex justify-content-center align-items-center">
                    <div
                      onClick={() => {
                        if (!activeKey) {
                          setActiveKey("filter");
                        } else {
                          setActiveKey("");
                        }
                      }}
                      title={t("filter")}
                    >
                      <FilterCircle className="addcutmButton" />
                    </div>

                    <div className="reloadBtn">
                      {isLoading ? (
                        <Loader />
                      ) : (
                        <ArrowClockwise
                          className="arrowClock"
                          title={t("refresh")}
                          onClick={() => reloadHandler()}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </FourGround>

              <FourGround>
                <div className="mt-2">
                  <Accordion alwaysOpen activeKey={activeKey}>
                    <Accordion.Item eventKey="filter">
                      <Accordion.Body>
                        <DataFilter
                          filterData={users}
                          setFilterData={setOltUser}
                          filterOptions={filterOptions}
                          setFilterOption={setFilterOption}
                          inputPermission={filterInputPermission}
                        />
                      </Accordion.Body>
                    </Accordion.Item>
                  </Accordion>

                  <div className="collectorWrapper pb-2">
                    <Table
                      isLoading={isLoading}
                      columns={columns}
                      data={oltUsers}
                    />
                  </div>
                </div>
              </FourGround>
              <Footer />
            </FontColor>
          </div>
        </div>
      </div>
    </>
  );
};

export default OLTUsers;
